import { intlLocale, type Locale, type NumeralSystem } from "./i18n";

/**
 * Money, in minor units, with no float anywhere near it.
 *
 * The rule the whole module exists to keep: **an amount is an integer count of
 * the currency's smallest unit**, and it stays one from the form field to the
 * database. `0.1 + 0.2` is `0.30000000000000004` in every JavaScript engine
 * there is, and an invoice that is off by a hundredth of a cent for one line in
 * ten thousand is a support conversation nobody can reproduce.
 *
 * So {@link parseMoney} does arithmetic on digit *strings* — it never builds an
 * intermediate float — and returns `amountMinor` directly.
 *
 * The minor-unit exponent is asked of `Intl` rather than kept in a table here.
 * USD has two, JPY has none, and Lebanon's own LBP has none either: `1000 LBP`
 * is a thousand, not ten. A table of our own would be a second opinion about
 * something CLDR already knows and updates.
 */

/** An amount, as a count of the currency's smallest unit. */
export interface Money {
	/** Cents for USD, whole units for LBP and JPY. Always an integer. */
	amountMinor: number;
	/** ISO 4217, uppercase. */
	currency: string;
}

/**
 * How many decimal places a currency has.
 *
 * `Intl` is the source. An unrecognised code falls back to two, which is the
 * majority and the safer direction: treating a two-decimal currency as having
 * none would multiply every amount by a hundred.
 */
export function minorUnits(currency: string): number {
	try {
		return (
			new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
				.maximumFractionDigits ?? 2
		);
	} catch {
		return 2;
	}
}

/**
 * Renders an amount for reading.
 *
 * Latin digits by default, in Arabic as in English — see `intlLocale`. A
 * warehouse reads a price off a printed label and compares it against a screen,
 * and the two should not be in different scripts.
 */
export function formatMoney(
	money: Money,
	locale: Locale,
	numerals: NumeralSystem = "latn",
): string {
	const digits = minorUnits(money.currency);

	// The one division in this module, and it is safe: `Intl` rounds to exactly
	// `digits` places, which recovers the integer the division came from for
	// every amount a business can hold. (The float only loses the last place
	// above 2^53 minor units — ninety trillion dollars.)
	const major = money.amountMinor / 10 ** digits;

	try {
		return new Intl.NumberFormat(intlLocale(locale, numerals), {
			style: "currency",
			currency: money.currency,
		}).format(major);
	} catch {
		// An unknown currency code. Showing the digits with the code beside them
		// beats throwing inside a table cell.
		return `${major.toFixed(digits)} ${money.currency}`;
	}
}

/** The digit ranges a person can type, mapped to 0-9. */
const DIGIT_OFFSETS = [
	{ zero: 0x0660, label: "arabic-indic" }, // ٠١٢٣٤٥٦٧٨٩
	{ zero: 0x06f0, label: "extended-arabic-indic" }, // ۰۱۲۳۴۵۶۷۸۹
];

/**
 * Rewrites Arabic-Indic digits as ASCII.
 *
 * Both ranges, because they look nearly identical and a keyboard decides which
 * one you get: `٤` (U+0664) and `۴` (U+06F4) are both four, and accepting one
 * while rejecting the other is a form that works for some Arabic speakers and
 * not others.
 */
export function toAsciiDigits(input: string): string {
	let out = "";
	for (const char of input) {
		const code = char.codePointAt(0) ?? 0;
		const range = DIGIT_OFFSETS.find(({ zero }) => code >= zero && code <= zero + 9);
		out += range === undefined ? char : String(code - range.zero);
	}
	return out;
}

/** The group and decimal separators a locale uses, read from `Intl` rather than assumed. */
function separatorsOf(locale: Locale): { group: string; decimal: string } {
	const parts = new Intl.NumberFormat(intlLocale(locale)).formatToParts(12345.6);
	return {
		group: parts.find((p) => p.type === "group")?.value ?? ",",
		decimal: parts.find((p) => p.type === "decimal")?.value ?? ".",
	};
}

/** Separators a person might type regardless of locale, plus the Arabic ones. */
const ARABIC_DECIMAL = "٫"; // ٫
const ARABIC_GROUP = "٬"; // ٬

/**
 * Reads a typed amount into minor units, or `null` if it is not one.
 *
 * `null` rather than a throw or a zero. A half-typed field is the normal state
 * of a form, not an exception, and a zero would silently become a real amount
 * the moment somebody pressed Save.
 *
 * What it accepts, deliberately: the locale's own separators, the Arabic
 * decimal and thousands marks, Arabic-Indic digits in either range, a leading
 * minus, and whitespace or a currency symbol around the number. What it
 * refuses: more decimal places than the currency has. `1,250.505` in USD is not
 * a rounding question — it is either a typo or the wrong currency, and guessing
 * which one is how a bug becomes a discrepancy of half a cent per line.
 */
export function parseMoney(input: string, currency: string, locale: Locale): number | null {
	const digits = minorUnits(currency);
	const { group, decimal } = separatorsOf(locale);

	let text = toAsciiDigits(input).trim();
	if (text === "") {
		return null;
	}

	const negative = text.startsWith("-");
	if (negative || text.startsWith("+")) {
		text = text.slice(1).trim();
	}

	// The decimal mark is normalised to "." first, so a locale whose group
	// separator is "." (de-DE) cannot have its decimal comma stripped as a
	// group mark.
	text = text.split(decimal).join(".").split(ARABIC_DECIMAL).join(".");
	text = text.split(group).join("").split(ARABIC_GROUP).join("");

	// Anything left that is not a digit or the single decimal point — a currency
	// symbol, a stray letter, a second point — and this is not an amount.
	if (!/^\d*(\.\d*)?$/.test(text) || text === "." || text === "") {
		return null;
	}

	const [whole = "", fraction = ""] = text.split(".");
	if (fraction.length > digits) {
		return null;
	}
	if (whole === "" && fraction === "") {
		return null;
	}

	// String concatenation, not multiplication: the fraction is padded to the
	// currency's width and glued on, so the result is an integer built from
	// characters and never passes through a float.
	const minor = Number(`${whole || "0"}${fraction.padEnd(digits, "0")}`);
	if (!Number.isSafeInteger(minor)) {
		return null;
	}
	return negative ? -minor : minor;
}
